import type { UtmChannelSnapshot } from "@/lib/utmChannelCookies";

/** API values for `lead_source` (mandatory). */
export type LeadSourceKind = "Organic" | "In organic";

/** API values for `lead_sub_source` when `lead_source` is Organic. */
export type OrganicLeadSubSource = "Direct" | "Search" | "refferal" | "Call Center";

/** API values for `lead_sub_source` when `lead_source` is InOrganic. */
export type InOrganicLeadSubSource =
  | "Google Ads"
  | "Meta Ads"
  | "TickTok Ads"
  | "Affliates"
  | "WhatsApp";

export type LeadSubSource = OrganicLeadSubSource | InOrganicLeadSubSource;

/** API values for `platform` (mandatory). */
export type LeadPlatform = "Web" | "WAP" | "Android" | "IOS";

export type LeadAttributionFields = {
  leadSource: LeadSourceKind;
  leadSubSource?: LeadSubSource;
  platform: LeadPlatform;
};

const PAID_MEDIUM_HINTS = [
  "cpc",
  "ppc",
  "paid",
  "paidsearch",
  "display",
  "banner",
  "retargeting",
  "cpm",
  "cpv",
];

function norm(value: string | undefined): string {
  return (value ?? "").trim().toLowerCase();
}

function hasPaidMedium(medium: string): boolean {
  if (!medium) return false;
  return PAID_MEDIUM_HINTS.some((hint) => medium.includes(hint));
}

function isMetaSource(source: string): boolean {
  return ["facebook", "meta", "instagram", "fb", "ig"].some((s) =>
    source.includes(s)
  );
}

function isGoogleSource(source: string): boolean {
  return source.includes("google") || source === "gclid";
}

function isTikTokSource(source: string): boolean {
  return source.includes("tiktok") || source.includes("ticktok");
}

function utmFromParams(
  utmParams?: Record<string, string>
): { source: string; medium: string } {
  return {
    source: norm(utmParams?.utm_source),
    medium: norm(utmParams?.utm_medium),
  };
}

export function utmFromSnapshot(last: UtmChannelSnapshot): {
  source: string;
  medium: string;
} {
  return { source: norm(last.source), medium: norm(last.medium) };
}

/**
 * Maps last-touch UTM (or channel cookies) to `lead_source` / `lead_sub_source`
 * per the lead API spec.
 */
export function resolveLeadAttribution(input?: {
  utmParams?: Record<string, string>;
  snapshot?: UtmChannelSnapshot;
}): Pick<LeadAttributionFields, "leadSource" | "leadSubSource"> {
  const { source, medium } = input?.utmParams
    ? utmFromParams(input.utmParams)
    : input?.snapshot
      ? utmFromSnapshot(input.snapshot)
      : { source: "", medium: "" };

  if (source.includes("whatsapp") || medium.includes("whatsapp")) {
    return { leadSource: "In organic", leadSubSource: "WhatsApp" };
  }

  if (isTikTokSource(source)) {
    return { leadSource: "In organic", leadSubSource: "TickTok Ads" };
  }

  if (isMetaSource(source) && hasPaidMedium(medium)) {
    return { leadSource: "In organic", leadSubSource: "Meta Ads" };
  }

  if (isGoogleSource(source) && hasPaidMedium(medium)) {
    return { leadSource: "In organic", leadSubSource: "Google Ads" };
  }

  if (
    medium.includes("affiliate") ||
    source.includes("affiliate") ||
    medium === "affiliates"
  ) {
    return { leadSource: "In organic", leadSubSource: "Affliates" };
  }

  if (hasPaidMedium(medium)) {
    if (isGoogleSource(source)) {
      return { leadSource: "In organic", leadSubSource: "Google Ads" };
    }
    if (isMetaSource(source)) {
      return { leadSource: "In organic", leadSubSource: "Meta Ads" };
    }
    if (isTikTokSource(source)) {
      return { leadSource: "In organic", leadSubSource: "TickTok Ads" };
    }
    return { leadSource: "In organic", leadSubSource: "Google Ads" };
  }

  if (
    medium.includes("call") ||
    source.includes("callcenter") ||
    source.includes("call_center") ||
    source.includes("call center")
  ) {
    return { leadSource: "Organic", leadSubSource: "Call Center" };
  }

  if (medium === "organic" || source.includes("organic")) {
    return { leadSource: "Organic", leadSubSource: "Search" };
  }

  if (medium === "referral" || medium === "refferal" || source === "referral") {
    return { leadSource: "Organic", leadSubSource: "refferal" };
  }

  if (!source && !medium) {
    return { leadSource: "Organic", leadSubSource: "Direct" };
  }

  return { leadSource: "Organic", leadSubSource: "Direct" };
}

/** Website clients send `Web` or `WAP` based on user agent. */
export function resolveLeadPlatform(): LeadPlatform {
  if (typeof navigator === "undefined") return "Web";
  const ua = navigator.userAgent;
  const isMobile =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
  return isMobile ? "WAP" : "Web";
}

export function buildLeadAttributionFields(input?: {
  utmParams?: Record<string, string>;
  snapshot?: UtmChannelSnapshot;
}): LeadAttributionFields {
  return {
    ...resolveLeadAttribution(input),
    platform: resolveLeadPlatform(),
  };
}
